import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import './AdminCreatorContributionsPage.css';

interface CreatorRow {
    creatorId: string;
    creatorName: string;
    creatorEmail: string;
    creatorAvatarUrl: string | null;
    totalSeconds: number;
    courseCount: number;
    uniqueLearners: number;
    percentage: number;
}

interface AllCreatorsResponse {
    month: string;
    systemTotalSeconds: number;
    creatorCount: number;
    creators: CreatorRow[];
}

const AdminCreatorContributionsPage: React.FC = () => {
    const { t } = useTranslation(['admin', 'profile']);
    const navigate = useNavigate();

    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    const [data, setData] = useState<AllCreatorsResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

    const fetchData = useCallback(async (m: string) => {
        setLoading(true);
        try {
            const res = await apiClient.get<AllCreatorsResponse>(
                '/creators/admin/all-learning-time',
                { params: { month: m } },
            );
            setData(res.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(monthKey);
    }, [monthKey, fetchData]);

    const goPrev = () => {
        if (month === 1) {
            setYear((y) => y - 1);
            setMonth(12);
        } else {
            setMonth((m) => m - 1);
        }
    };

    const goNext = () => {
        if (isCurrentMonth) return;
        if (month === 12) {
            setYear((y) => y + 1);
            setMonth(1);
        } else {
            setMonth((m) => m + 1);
        }
    };

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        if (minutes >= 60) {
            return t('profile:stats.timeShortHoursMinutes', {
                h: Math.floor(minutes / 60),
                m: minutes % 60,
            });
        }
        return t('profile:stats.timeShortMinutes', { value: minutes });
    };

    const getMonthLabel = (): string => {
        const date = new Date(year, month - 1, 1);
        return date.toLocaleDateString('en', { year: 'numeric', month: 'long' });
    };

    const getRankClass = (index: number): string => {
        if (index === 0) return 'top-1';
        if (index === 1) return 'top-2';
        if (index === 2) return 'top-3';
        return '';
    };

    return (
        <div className="admin-cc-page">
            {/* Header */}
            <div className="admin-cc-header">
                <button className="admin-cc-back" onClick={() => navigate(-1)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1>{t('admin:creatorContributions.title')}</h1>
            </div>

            {/* Month Selector */}
            <div className="admin-cc-month-selector">
                <button className="admin-cc-month-btn" onClick={goPrev}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <span className="admin-cc-month-label">{getMonthLabel()}</span>
                <button
                    className="admin-cc-month-btn"
                    onClick={goNext}
                    disabled={isCurrentMonth}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {loading ? (
                <div className="admin-cc-loading">
                    <div className="loading-spinner" />
                    <span>{t('admin:creatorContributions.loading')}</span>
                </div>
            ) : data ? (
                <>
                    {/* Summary */}
                    <div className="admin-cc-summary">
                        <div className="admin-cc-summary-card highlight">
                            <div className="admin-cc-summary-value">
                                {formatTime(data.systemTotalSeconds)}
                            </div>
                            <div className="admin-cc-summary-label">
                                {t('admin:creatorContributions.systemTotal')}
                            </div>
                        </div>
                        <div className="admin-cc-summary-card">
                            <div className="admin-cc-summary-value">
                                {data.creatorCount}
                            </div>
                            <div className="admin-cc-summary-label">
                                {t('admin:creatorContributions.activeCreators')}
                            </div>
                        </div>
                        <div className="admin-cc-summary-card">
                            <div className="admin-cc-summary-value">
                                {data.creators.reduce((sum, c) => sum + c.uniqueLearners, 0)}
                            </div>
                            <div className="admin-cc-summary-label">
                                {t('admin:creatorContributions.totalLearners')}
                            </div>
                        </div>
                    </div>

                    {/* Creator List */}
                    {data.creators.length > 0 ? (
                        <>
                            <h2 className="admin-cc-section-title">
                                {t('admin:creatorContributions.ranking')}
                            </h2>
                            <div className="admin-cc-list">
                                {data.creators.map((creator, index) => (
                                    <div
                                        key={creator.creatorId}
                                        className="admin-cc-creator-row"
                                        onClick={() => navigate(`/admin/learning-time?userId=${creator.creatorId}`)}
                                    >
                                        <span className={`admin-cc-rank ${getRankClass(index)}`}>
                                            {index + 1}
                                        </span>
                                        <div className="admin-cc-avatar">
                                            {creator.creatorAvatarUrl ? (
                                                <img src={creator.creatorAvatarUrl} alt="" />
                                            ) : (
                                                <span>
                                                    {creator.creatorName.charAt(0).toUpperCase()}
                                                </span>
                                            )}
                                        </div>
                                        <div className="admin-cc-creator-info">
                                            <div className="admin-cc-creator-name">
                                                {creator.creatorName}
                                            </div>
                                            <div className="admin-cc-creator-meta">
                                                {t('admin:creatorContributions.coursesAndLearners', {
                                                    courses: creator.courseCount,
                                                    learners: creator.uniqueLearners,
                                                })}
                                            </div>
                                        </div>
                                        <div className="admin-cc-creator-stats">
                                            <div className="admin-cc-creator-time">
                                                {formatTime(creator.totalSeconds)}
                                            </div>
                                            <div className="admin-cc-creator-pct">
                                                {creator.percentage}%
                                            </div>
                                            <div className="admin-cc-pct-bar">
                                                <div
                                                    className="admin-cc-pct-fill"
                                                    style={{ width: `${Math.min(creator.percentage, 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="admin-cc-empty">
                            <div className="admin-cc-empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <p>{t('admin:creatorContributions.noData')}</p>
                        </div>
                    )}
                </>
            ) : (
                <div className="admin-cc-empty">
                    <div className="admin-cc-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <p>{t('admin:creatorContributions.loadError')}</p>
                </div>
            )}
        </div>
    );
};

export default AdminCreatorContributionsPage;
